// @ts-strict

import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ICampaignConfigFieldData } from '@app/core/models/core-models';
import { LocalStorageService } from '@services/local-storage/local-storage.service';

export interface TextareaInputValueEmitter {
  value: string;
  system?: string;
  isValid: boolean;
}

@Component({
  selector: 'app-textarea-input',
  templateUrl: './textarea-input.component.html',
  styleUrls: ['./textarea-input.component.scss']
})
export class TextareaInputComponent implements OnInit, OnChanges {
  @Input() initialValue: string = '';
  @Input() fieldInfo?: ICampaignConfigFieldData;
  @Input() configurationData?: ICampaignConfigFieldData[];
  @Input() placeholder?: string;
  @Input() submitted = false;
  @Input() inputName?: string;
  @Input() componentLoaded = false;
  @Input() mode?: string;
  @Input() toolTipContent?: string;
  @Input() minLengthValue?: number;
  @Input() requiredValue = false;
  @Input() noLabel = false;
  @Input() autoFocus = false;
  @Input() lookForChanges = false;
  @Input() fontFamily?: string;

  @Output() emitValue = new EventEmitter<TextareaInputValueEmitter>();

  private isFormFilled = false;
  public textareaInput!: UntypedFormGroup;
  public minLength = { value: 3 };
  public panelLanguage = '';
  public isFocused = false;
  public fieldName = '';
  public inputLengthChecked = false;

  get newCampaignMode(): boolean {
    return this.mode === 'new-campaign';
  }

  constructor(private localStorageService: LocalStorageService, private readonly formBuilder: UntypedFormBuilder) {}

  get configForm() {
    return this.textareaInput.controls;
  }

  ngOnInit(): void {
    this.textareaInput = this.formBuilder.group({
      textarea: [this.initialValue]
    });

    this.panelLanguage = this.localStorageService.getPanelLang();
    if (this.fieldInfo?.translations) {
      this.fieldName = this.fieldInfo?.translations?.find(element => element.lang === this.panelLanguage)?.label || '';
    } else if (this.inputName) {
      this.fieldName = this.inputName;
    }
    this.localStorageService.panelLanguage.subscribe(panelLang => {
      if (panelLang) {
        this.panelLanguage = panelLang;
      }
    });

    this.minLength = this.minLengthValue ? { value: this.minLengthValue } : { value: 3 };

    if (this.requiredValue) {
      this.textareaInput.get('textarea')?.setValidators([Validators.required, Validators.minLength(this.minLength.value)]);
    }

    if (this.configurationData?.length) {
      const currentConfig = this.configurationData.find(value => value.system_name === this.fieldInfo?.system_name);
      if (currentConfig) {
        this.textareaInput.get('textarea')?.patchValue(currentConfig.config_values);
        this.getValue();
      }
    }
  }

  ngOnChanges(): void {
    if ((this.configurationData?.length && this.componentLoaded && !this.isFormFilled) || (this.lookForChanges && this.textareaInput?.get('textarea'))) {
      this.isFormFilled = true;
      const currentConfig = this.configurationData?.find(value => value.system_name === this.fieldInfo?.system_name);

      if (currentConfig?.config_values && this.textareaInput?.get('textarea')) {
        this.textareaInput?.get('textarea')?.patchValue(currentConfig.config_values);
        this.getValue();
      }
    }
  }

  public getValue(): void {
    this.emitValue.emit({
      value: this.configForm.textarea.value as string,
      system: this.fieldInfo?.system_name,
      isValid: this.configForm.textarea.valid
    });
  }

  public onInputBlur(): void {
    this.isFocused = false;
    const inputLength = this.configForm.textarea.value?.length;
    const requiredError = this.configForm.textarea.errors?.required;
    if (inputLength > 0 || requiredError) {
      this.inputLengthChecked = true;
    } else {
      this.inputLengthChecked = false;
    }
  }
}
